import {
    HttpClient,
    HttpHandlerFn,
    HttpHeaders,
    HttpParams,
    HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ToastMessageOptions } from 'primeng/api';

export type HttpErrorToastMessageType = (
    message: ToastMessageOptions,
    source?: 'error-interceptor'
) => void;
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
    constructor(private http: HttpClient) {}

    HttpErrorToastMessage?: HttpErrorToastMessageType;
    /**
     *
     * Authorize the device to gain access to the backend API like a user login.
     *
     * The method calls the '/token' endpoint and in a successful return sets the local storage.
     *
     * The localstorage keys are: 'token', 'token_expires_in', 'access_token', 'refresh_token'.
     *
     * As Bearer authentication use the 'access_token'.
     *
     * @param userName The Device Id
     * @param password The auto generated by server passowrd. For the devices this is a very long string (256 characters) generated by the AuthorizeMerchantTellerPlugin end point
     * @returns
     */
    public auhtorizeUser(userName: string, password: string, taxId: string) {
        const body = new HttpParams()
            .set('grant_type', 'password')
            .set('client_id', 'postman')
            .set('client_secret', 'postman_secret')
            .set('scope', 'profile offline_access email roles openid') // Remove openid if the client does not need to store the id_token.
            .set('username', userName)
            .set('password', password)
            .set('taxId', taxId);

        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded'
        );

        return this.http
            .post<any>(`${environment.apiURI}/token`, body, { headers })
            .pipe(
                map((token) => {
                    localStorage.setItem('token', JSON.stringify(token));
                    localStorage.setItem('token_expires_in', token.expires_in);
                    localStorage.setItem('access_token', token.access_token);
                    localStorage.setItem('refresh_token', token.refresh_token);
                    localStorage.setItem('id_token', token.id_token);
                    return token;
                })
            );
    }

    /**
     * Authorize device using a refresh_token
     * @param request
     * @param next
     * @returns
     */
    public reauthorizeUser(request: HttpRequest<unknown>, next: HttpHandlerFn) {
        const refreshToken = localStorage.getItem('refresh_token');
        const body = new HttpParams()
            .set('grant_type', 'refresh_token')
            .set('client_id', 'postman')
            .set('client_secret', 'postman_secret')
            .set('scope', 'profile offline_access email roles openid') // Remove openid if the client does not need to store the id_token.
            .set('refresh_token', refreshToken ?? 'invalid_refresh_token');

        const headers = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded')
            .set('X-Platform', environment.XPlatform)
            .set('X-PlatformOwner', environment.XPlatformOwner)
            .set('X-PlatformAuthor', environment.XPlatformAuthor);

        return this.http
            .post<any>(`${environment.apiURI}/token`, body, { headers })
            .pipe(
                map((token) => {
                    localStorage.setItem('token', JSON.stringify(token));
                    localStorage.setItem('token_expires_in', token.expires_in);
                    localStorage.setItem('access_token', token.access_token);
                    localStorage.setItem('refresh_token', token.refresh_token);
                    localStorage.setItem('id_token', token.id_token);

                    request = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${token.access_token}`,
                            'Access-Control-Allow-Origin': '*',
                            'Cache-Control':
                                'no-cache, no-store, must-revalidate',
                            'access-control-expose-headers': 'Www-Authenticate',
                            'X-Platform': environment.XPlatform,
                            'X-PlatformOwner': environment.XPlatformOwner,
                            'X-PlatformAuthor': environment.XPlatformAuthor
                            // 'Access-Control-Allow-Origin': 'http://localhost:4200',
                            // 'Access-Control-Allow-Credentials': 'true',
                            // 'Access-Control-Allow-Headers': 'Content-Type, Authorization'
                        }
                    });

                    return request;
                })
            );
    }

    public revokeUser() {
        const accessToken = localStorage.getItem('access_token');
        const body = new HttpParams()
            .set('client_id', 'postman')
            .set('client_secret', 'postman_secret')
            .set('token', accessToken ?? 'invalid_token');

        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded'
        );

        return this.http.post<any>(`${environment.apiURI}/revoke`, body, {
            headers
        });

        //   .subscribe((result) => {
        //     // localStorage.removeItem('token_expires_in');
        //     // localStorage.removeItem('access_token');
        //     // localStorage.removeItem('refresh_token');
        //     // localStorage.removeItem('id_token');
        //     // localStorage.removeItem('token');
        //   });
    }

    public introspectUserToken() {
        const accessToken = localStorage.getItem('access_token');
        const body = new HttpParams()
            .set('client_id', 'postman')
            .set('client_secret', 'postman_secret')
            .set('token', accessToken ?? 'invalid_token');

        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded'
        );

        return this.http.post<any>(
            `${environment.apiURI}/token/introspect`,
            body,
            {
                headers
            }
        );
    }

    public isLoggedIn() {
        const token = localStorage.getItem('token'); // get token from local storage
        if (token == undefined) return false;

        const payload = atob(token.split('.')[1]); // decode payload of token
        if (payload == undefined || payload == '') return false;

        const parsedPayload = JSON.parse(payload); // convert payload into an Object
        return parsedPayload.exp > Date.now() / 1000; // check if token is expired
    }
}
